/**
 * Arreglo 1 (oct-2026) — Dock a pantalla completa del barbero: Agenda · Mis clientes · Venta.
 * Se abre encima de la pantalla actual (sin navegar): al cerrar (X o atrás) vuelves exactamente donde
 * estabas, con el mismo scroll. Reutiliza la agenda real del perfil (PublicAgendaTimeline, owner).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CalendarDays, Users, Receipt, X, QrCode } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { PublicAgendaTimeline } from '@/components/Profile/PublicAgendaTimeline';
import { ClientsPanel } from '@/components/Clients/ClientsPanel';
import { BookingShareSheet } from '@/components/Booking/BookingShareSheet';
import { WalkInSalePanel } from './WalkInSalePanel';
import { tr } from '@/contexts/LanguageContext';

const TABS = [
  { id: 'agenda', label: tr("Agenda"), icon: CalendarDays },
  { id: 'clients', label: tr("Mis clientes"), icon: Users },
  { id: 'sale', label: tr("Venta"), icon: Receipt },
];

export const AgendaDock = ({ open, onClose, initialTab = 'agenda' }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [tab, setTab] = useState(initialTab);
  const [saleClient, setSaleClient] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const pathRef = useRef(location.pathname);
  const pushedRef = useRef(false);

  useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);

  // Atrás del navegador/teléfono cierra el dock (sin tocar la pantalla de abajo).
  useEffect(() => {
    if (!open) return undefined;
    window.history.pushState({ lbwDock: true }, '');
    pushedRef.current = true;
    const onPop = () => { pushedRef.current = false; onClose?.(); };
    window.addEventListener('popstate', onPop);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('lbw-dock-open');
    return () => {
      window.removeEventListener('popstate', onPop);
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('lbw-dock-open');
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navegar desde dentro (ver perfil, ajustes…) cierra el dock.
  useEffect(() => {
    if (open && pathRef.current !== location.pathname) { pushedRef.current = false; onClose?.(); }
    pathRef.current = location.pathname;
  }, [location.pathname, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = useCallback(() => {
    if (pushedRef.current && window.history.state?.lbwDock) { window.history.back(); return; }
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!user) return null;
  const node = (
    <AnimatePresence>
      {open && (
        <motion.div
          key="agenda-dock"
          initial={{ y: '100%', opacity: 0.6 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0.6 }}
          transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          className="fixed inset-0 z-[45] bg-background flex flex-col"
          role="dialog"
          aria-modal="true"
          data-testid="agenda-dock"
        >
          <header className="flex items-center justify-between gap-3 px-4 pt-[max(env(safe-area-inset-top),12px)] pb-2 border-b border-border bg-background/95 backdrop-blur">
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.25em] text-primary font-bold">{tr("Mi negocio")}</p>
              <h2 className="font-display text-lg font-bold text-foreground truncate" data-testid="agenda-dock-title">
                {TABS.find((t) => t.id === tab)?.label}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setShareOpen(true)} className="w-10 h-10 rounded-full bg-primary/15 border border-primary/40 text-primary flex items-center justify-center hover:bg-primary/25" aria-label={tr("Mi link y QR de reservas")} title={tr("Mi link y QR de reservas")} data-testid="agenda-dock-share-btn">
                <QrCode className="w-5 h-5" />
              </button>
              <button type="button" onClick={close} className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center hover:bg-secondary/70" aria-label={tr("Cerrar")} data-testid="agenda-dock-close">
                <X className="w-5 h-5" />
              </button>
            </div>
          </header>
          <nav className="grid grid-cols-3 gap-1 px-3 py-2 border-b border-border" data-testid="agenda-dock-tabs">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button key={id} type="button" onClick={() => setTab(id)} aria-pressed={tab === id} data-testid={`agenda-dock-tab-${id}`}
                className={`h-10 rounded-full text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors ${tab === id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'}`}>
                <Icon className="w-4 h-4" /> {label}
              </button>
            ))}
          </nav>
          <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-3 pb-[max(env(safe-area-inset-bottom),16px)]" data-testid={`agenda-dock-panel-${tab}`}>
            {tab === 'agenda' && <PublicAgendaTimeline barber={user} isOwner />}
            {tab === 'clients' && (
              <ClientsPanel
                onBookFor={() => setTab('agenda')}
                onSellFor={(c) => { setSaleClient(c); setTab('sale'); }}
              />
            )}
            {tab === 'sale' && <WalkInSalePanel user={user} presetClient={saleClient} onReset={() => setSaleClient(null)} />}
          </div>
          <BookingShareSheet open={shareOpen} onOpenChange={setShareOpen} username={user?.username} name={user?.name || user?.username} avatar={user?.avatar} kind="barber" />
        </motion.div>
      )}
    </AnimatePresence>
  );
  return createPortal(node, document.body);
};

export default AgendaDock;
